import React, { ReactNode, useMemo } from "react"
import { useTranslation } from "react-i18next"

import styled from "styled-components/native"

import { useActiveProfileId } from "../hooks/use-active-profile-id"
import { useProgressItem } from "../hooks/use-progress"
import {
  getTracksFromConsumableContent,
  ConsumableContent,
  getDurationFromConsumableContent,
} from "../lib/consumable-content"
import {
  PlayableProgressItem,
  EbookProgressItem,
  getProgressItemKeyFromConsumableContent,
} from "../lib/progress-item"
import { BoxPadding } from "./box"
import ProgressBar from "./progress-bar"
import ProgressFinishedBadge from "./progress-finished-badge"
import Row from "./row"
import Text from "./text"
import Time from "./time"

const ProgressBarContainer = styled.View`
  flex: 1;
`

function DurationLabel({
  units,
  value,
}: {
  units: "pages" | "milliseconds"
  value: number
}) {
  const { t } = useTranslation()
  switch (units) {
    case "pages":
      return (
        <Text textStyle="caption" color="secondary">
          {t("{{number}} pages", { number: value })}
        </Text>
      )
    case "milliseconds":
      return (
        <Time units textStyle="caption" color="secondary">
          {value}
        </Time>
      )
  }
}

type ProgressProps = {
  consumableContent?: ConsumableContent
  flex?: number
  includeFinishedBadge?: boolean
  includeProgressBar?: boolean
  includeFinishedProgressBar?: boolean
  includeProgressLabel?: boolean
  includeDurationLabel?: boolean
  paddingTop?: BoxPadding
  paddingBottom?: BoxPadding
  paddingLeft?: BoxPadding
  paddingRight?: BoxPadding
  alignment?: "left" | "right"
  childrenBefore?: ReactNode
  childrenAfter?: ReactNode
  className?: string
}

/**
 * Displays real-time progress for playable content. Can be configured to show
 * any combination of time (duration or time left), a finished badge, and a
 * linear progress bar.
 */
export default function ProgressForConsumableContent({
  consumableContent,
  flex,
  includeFinishedBadge,
  includeProgressBar,
  includeFinishedProgressBar,
  includeProgressLabel,
  includeDurationLabel,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  alignment = "left",
  childrenBefore = null,
  childrenAfter = null,
}: ProgressProps): React.JSX.Element | null {
  const profileId = useActiveProfileId()
  const key = consumableContent
    ? getProgressItemKeyFromConsumableContent(consumableContent)
    : undefined
  const progressItem = useProgressItem({ key })
  const tracks = useMemo(
    () => getTracksFromConsumableContent({ consumableContent, profileId }),
    [consumableContent, profileId],
  )

  const { t } = useTranslation()

  const getProgressBar = ({
    finished,
    position,
    duration,
    percent,
  }: {
    finished: boolean
    position?: number
    duration?: number
    percent?: number
  }): ReactNode | undefined => {
    if (!includeProgressBar) {
      return undefined
    }

    if (finished) {
      if (!includeFinishedProgressBar) {
        return undefined
      }

      return <ProgressBar progress={1} />
    }

    if (percent) {
      return <ProgressBar progress={percent} />
    }

    if (position && duration) {
      return <ProgressBar progress={position / duration} />
    }

    return undefined
  }

  let finishedBadge: ReactNode | undefined
  let progressBar: ReactNode | undefined
  let progressLabel: ReactNode | undefined

  // Progress elements for "playable" content (e.g. videos, audiobooks)
  if (progressItem instanceof PlayableProgressItem) {
    const { finished } = progressItem.getProgress()
    const duration = progressItem.getContentDuration(tracks) || 0
    const position = progressItem.getContentPosition(tracks) || 0

    finishedBadge =
      includeFinishedBadge && finished ? <ProgressFinishedBadge /> : undefined
    progressLabel =
      includeProgressLabel && duration && position && !finished ? (
        <Time
          units
          textStyle="caption"
          color="secondary"
          stringKey="{{time}} left"
        >
          {
            // HACK: On occasion the position will be greater than the
            // duration at the very end of a track. This is because position
            // is always exact while duration is sometimes an estimate.
            Math.max(duration - position, 0)
          }
        </Time>
      ) : undefined
    progressBar = getProgressBar({ finished, position, duration })
  }
  // Progress elements for ebooks
  else if (
    progressItem instanceof EbookProgressItem &&
    consumableContent?.type === "ebook"
  ) {
    const { percent, finished } = progressItem.getProgress()
    finishedBadge =
      includeFinishedBadge && finished ? <ProgressFinishedBadge /> : undefined
    progressLabel =
      includeProgressLabel && percent && !finished ? (
        <Text textStyle="caption" color="secondary">
          {t("{{number}}% complete", { number: Math.round(percent * 100) })}
        </Text>
      ) : undefined

    progressBar = getProgressBar({ finished, percent })
  }

  const displayDuration =
    (includeProgressLabel || includeDurationLabel) && !progressLabel
      ? getDurationFromConsumableContent(consumableContent)
      : undefined
  const durationLabel = displayDuration ? (
    <DurationLabel {...displayDuration} />
  ) : null

  return finishedBadge || progressLabel || durationLabel || progressBar ? (
    <>
      {childrenBefore}
      <Row
        flex={flex}
        alignItems="center"
        justifyContent={alignment == "left" ? "flex-start" : "flex-end"}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        gap="small"
      >
        {finishedBadge || progressLabel || durationLabel ? (
          <Row alignItems="center" gap="tiny">
            {finishedBadge}
            {progressLabel || durationLabel}
          </Row>
        ) : null}
        {progressBar ? (
          <ProgressBarContainer>{progressBar}</ProgressBarContainer>
        ) : null}
      </Row>
      {childrenAfter}
    </>
  ) : null
}
